import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import styled from 'styled-components';

const ChartContainer = styled.div`
  width: 100%;
  height: ${props => props.height || '400px'};
  position: relative;

  svg {
    width: 100%;
    height: 100%;
  }

  .tooltip {
    position: absolute;
    background-color: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 12px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s;
    z-index: 1000;
  }

  .line {
    fill: none;
    stroke-width: 2px;
  }

  .dot {
    cursor: pointer;
    transition: r 0.2s;

    &:hover {
      r: 6;
    }
  }

  .axis text {
    font-family: 'Segoe UI', sans-serif;
    font-size: 12px;
  }

  .axis-label {
    font-family: 'Segoe UI', sans-serif;
    font-size: 14px;
    font-weight: 600;
  }

  .grid line {
    stroke: #e0e0e0;
    stroke-opacity: 0.7;
    shape-rendering: crispEdges;
  }

  .grid path {
    stroke-width: 0;
  }
`;

/**
 * D3 Line Chart Component
 * @param {Array} data - Array of objects with format: [{x: value, y: value, ...}]
 * @param {String} xKey - Key for x-axis values
 * @param {String} yKey - Key for y-axis values
 * @param {String} xLabel - Label for x-axis
 * @param {String} yLabel - Label for y-axis
 * @param {Function} onClick - Click handler receiving the clicked data point
 * @param {String} color - Line color (default: LinkedIn Blue)
 * @param {String} height - Chart height (default: 400px)
 * @param {Boolean} showDots - Show data points (default: true)
 * @param {Boolean} showGrid - Show grid lines (default: true)
 */
const D3LineChart = ({ 
  data = [], 
  xKey = 'x', 
  yKey = 'y', 
  xLabel = '', 
  yLabel = '',
  onClick = null,
  color = '#0077B5',
  height = '400px',
  showDots = true,
  showGrid = true
}) => {
  const svgRef = useRef();
  const tooltipRef = useRef();
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 800, height: 400 });

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        setDimensions({ width, height: parseInt(height) });
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
      handleResize();
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [height]);

  useEffect(() => {
    if (!data || data.length === 0 || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const margin = { top: 20, right: 30, bottom: 60, left: 60 };
    const width = dimensions.width - margin.left - margin.right;
    const chartHeight = dimensions.height - margin.top - margin.bottom;

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Determine if x-axis is date or numeric
    const isDate = data[0][xKey] instanceof Date || !isNaN(Date.parse(data[0][xKey]));
    
    const x = isDate
      ? d3.scaleTime()
          .domain(d3.extent(data, d => new Date(d[xKey])))
          .range([0, width])
      : d3.scaleLinear()
          .domain([0, d3.max(data, d => d[xKey])])
          .range([0, width]);

    const y = d3.scaleLinear()
      .domain([0, d3.max(data, d => d[yKey]) * 1.1])
      .range([chartHeight, 0]);

    // Add grid
    if (showGrid) {
      g.append('g')
        .attr('class', 'grid')
        .call(d3.axisLeft(y)
          .tickSize(-width)
          .tickFormat('')
        );
    }

    // X Axis
    const xAxis = isDate 
      ? d3.axisBottom(x).ticks(5).tickFormat(d3.timeFormat('%b %Y'))
      : d3.axisBottom(x).ticks(5);

    g.append('g')
      .attr('class', 'axis')
      .attr('transform', `translate(0,${chartHeight})`)
      .call(xAxis)
      .selectAll('text')
      .attr('transform', 'rotate(-45)')
      .style('text-anchor', 'end');

    // Y Axis
    g.append('g')
      .attr('class', 'axis')
      .call(d3.axisLeft(y).ticks(5));

    // Line generator
    const line = d3.line()
      .x(d => x(isDate ? new Date(d[xKey]) : d[xKey]))
      .y(d => y(d[yKey]))
      .curve(d3.curveMonotoneX);

    // Draw line with animation
    const path = g.append('path')
      .datum(data)
      .attr('class', 'line')
      .attr('d', line)
      .attr('stroke', color)
      .attr('stroke-width', 2)
      .attr('fill', 'none');

    const pathLength = path.node().getTotalLength();

    path
      .attr('stroke-dasharray', `${pathLength} ${pathLength}`)
      .attr('stroke-dashoffset', pathLength)
      .transition()
      .duration(1000)
      .ease(d3.easeLinear)
      .attr('stroke-dashoffset', 0);

    // Add dots
    if (showDots) {
      g.selectAll('.dot')
        .data(data)
        .enter()
        .append('circle')
        .attr('class', 'dot')
        .attr('cx', d => x(isDate ? new Date(d[xKey]) : d[xKey]))
        .attr('cy', d => y(d[yKey]))
        .attr('r', 0)
        .attr('fill', color)
        .on('click', (event, d) => {
          if (onClick) onClick(d);
        })
        .on('mouseover', (event, d) => {
          if (tooltipRef.current) {
            const xValue = isDate ? d3.timeFormat('%b %d, %Y')(new Date(d[xKey])) : d[xKey];
            d3.select(tooltipRef.current)
              .style('opacity', 1)
              .html(`<strong>${xLabel || 'X'}: ${xValue}</strong><br/>${yLabel || 'Y'}: ${d[yKey].toLocaleString()}`)
              .style('left', `${event.pageX + 10}px`)
              .style('top', `${event.pageY - 20}px`);
          }
        })
        .on('mouseout', () => {
          if (tooltipRef.current) {
            d3.select(tooltipRef.current).style('opacity', 0);
          }
        })
        .transition()
        .delay(1000)
        .duration(300)
        .attr('r', 4);
    }

    // X Axis Label
    if (xLabel) {
      g.append('text')
        .attr('class', 'axis-label')
        .attr('text-anchor', 'middle')
        .attr('x', width / 2)
        .attr('y', chartHeight + margin.bottom - 10)
        .text(xLabel);
    }

    // Y Axis Label
    if (yLabel) {
      g.append('text')
        .attr('class', 'axis-label')
        .attr('text-anchor', 'middle')
        .attr('transform', 'rotate(-90)')
        .attr('x', -chartHeight / 2)
        .attr('y', -margin.left + 15)
        .text(yLabel);
    }
  }, [data, dimensions, xKey, yKey, xLabel, yLabel, onClick, color, showDots, showGrid]);

  return (
    <ChartContainer ref={containerRef} height={height}>
      <svg ref={svgRef}></svg>
      <div ref={tooltipRef} className="tooltip"></div>
    </ChartContainer>
  );
};

export default D3LineChart;


